import { styled } from "goober";
import type { QueryRowType } from "@rocicorp/zero";
import { useMemo, useRef, useState } from "react";
import { useZero } from "@rocicorp/zero/react";
import { queries, roundedHoursAgo } from "../../../shared/queries";
import { sharedMutators } from "../../../shared/mutators";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";

import { spaceIcon } from "../../lib/spaceIcon";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { ListFilter, type FilterDef } from "../ListFilter";
import { ListTable, type SortDirection } from "../ListTable";
import { Skeleton } from "../Skeleton";
import { Stepper, StepButton } from "../Stepper";
import { META_SEP } from "../MetaSeparator";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { HelpTooltip } from "../HelpTooltip";
import {
  StatusBreakdownChart,
  type StatusSegment,
} from "../StatusBreakdownChart";
import { WorkflowBadge } from "../WorkflowBadge";
import { buildModelPricingLookup } from "../../../shared/aiModelPricing";
import type { AiUsageActivity } from "./aiUsageActivities";
import { AI_USAGE_ACTIVITY_LABELS } from "./aiUsageActivities";
import { StatCell } from "./AiOverviewStats";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { NameBody, NameCell, ScrollArea, TableCard } from "./monitoringList";
import { ScopeDot } from "./ScopeDot";
import {
  computeAiValueSummary,
  computeWorkflowAiValueRows,
  formatSignedValueUsd,
  formatValueUsd,
  productivityRateFromBudgets,
  type WorkflowAiValueRow,
  type WorkflowValueWorkflow,
} from "./workflowAiValue";
import { SPACE_COLORS } from "./monitoringPanelLayout";

type MonitoringWorkflow = QueryRowType<
  ReturnType<typeof queries.monitoringWorkflowValue>
>;

const DAY_MS = 86_400_000;
// The window is capped at 90 days because the day-grained usage query syncs
// every rollup row in the window to the client.
const WINDOW_OPTIONS = [
  { value: "30", label: "30 days", ms: 30 * DAY_MS },
  { value: "60", label: "60 days", ms: 60 * DAY_MS },
  { value: "90", label: "90 days", ms: 90 * DAY_MS },
] as const;

function windowMs(param: string): number {
  const option = WINDOW_OPTIONS.find((o) => o.value === param);
  return option?.ms ?? 30 * DAY_MS;
}

function windowLabel(param: string): string {
  const option = WINDOW_OPTIONS.find((o) => o.value === param);
  return option?.label ?? "30 days";
}

const RATE_STEP = 5;
const RATE_INPUT_STYLE = {
  minWidth: 0,
  width: 56,
  textAlign: "center",
  padding: "0 4px",
  flexShrink: 0,
} as const;

const Content = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryCard = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 32px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const SummaryHeader = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  flex-shrink: 0;
  container-type: inline-size;
`;

const StatRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  align-items: flex-start;
`;

const StatsRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(24px, 5cqi, 64px);
  align-items: stretch;
`;

const ValueBox = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
`;

const StatLabelRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const VALUE_STAT_TOOLTIPS = {
  total:
    "The total value these workflows have saved you over the selected time range, based on your hourly rate. It combines one-time build savings with ongoing run savings.",
  build:
    "The value of the time you saved building these workflows. It’s a one-time saving, based on your hourly rate.",
  run: "The value of the time these workflows save you each time they run, added up across every run in the selected time range.",
  workflows: "How many workflows saved you time over the selected time range.",
} as const;

function StatLabel({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <StatLabelRow>
      <Text variant="text-small" color="var(--text-secondary)">
        {label}
      </Text>
      <HelpTooltip content={tooltip} />
    </StatLabelRow>
  );
}

const MONITORING_GRID =
  "minmax(200px, 3fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(60px, 0.75fr)";

const SubValue = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
`;

const SumRow = styled("div")`
  display: grid;
  grid-template-columns: ${MONITORING_GRID};
  align-items: center;
  border-top: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  flex-shrink: 0;
`;

const SumCell = styled("div")`
  display: flex;
  align-items: center;
  padding: 14px 8px;

  &:first-child {
    padding-left: 24px;
  }
  &:last-child {
    padding-right: 24px;
  }
`;

function toWorkflowValueWorkflow(
  workflow: MonitoringWorkflow
): WorkflowValueWorkflow {
  return {
    id: workflow.id,
    name: workflow.name,
    spaceId: workflow.spaceId,
    estimatedMinutesSaved: workflow.estimatedMinutesSaved,
    estimatedBuildMinutesSaved: workflow.estimatedBuildMinutesSaved,
    timeSavedSource: workflow.timeSavedSource,
    timeSavedBuildSource: workflow.timeSavedBuildSource,
  };
}

type WorkflowValueRow = {
  workflowId: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  space: DisplayableSpace | null;
  branches: readonly { name: string }[];
  totalValueUsd: number;
  buildValueUsd: number;
  runValueUsd: number;
  buildMinutes: number | null;
  runMinutes: number | null;
  buildSource: "llm" | "manual" | null;
  runSource: "llm" | "manual" | null;
};

type SpaceValueRow = {
  spaceId: string;
  space: DisplayableSpace;
  label: string;
  totalValueUsd: number;
  buildValueUsd: number;
  runValueUsd: number;
  workflowCount: number;
};

function computeWorkflowValueRows(
  valueRows: readonly WorkflowAiValueRow[],
  workflows: readonly MonitoringWorkflow[]
): WorkflowValueRow[] {
  const workflowById = new Map(workflows.map((w) => [w.id, w]));

  const rows: WorkflowValueRow[] = [];
  for (const row of valueRows) {
    const wf = workflowById.get(row.workflowId);
    if (!wf) continue;
    rows.push({
      workflowId: row.workflowId,
      name: wf.name,
      spaceId: wf.spaceId,
      badgeSeed: wf.badgeSeed,
      pausedAt: wf.pausedAt,
      space: wf.space ?? null,
      branches: wf.branches ?? [],
      totalValueUsd: row.totalGrossValueUsd ?? 0,
      buildValueUsd: row.byActivity.build.grossValueUsd ?? 0,
      runValueUsd: row.byActivity.run.grossValueUsd ?? 0,
      buildMinutes: row.byActivity.build.minutesPerEvent,
      runMinutes: row.byActivity.run.minutesPerEvent,
      buildSource: row.byActivity.build.source,
      runSource: row.byActivity.run.source,
    });
  }

  return rows.sort(
    (a, b) => b.totalValueUsd - a.totalValueUsd || a.name.localeCompare(b.name)
  );
}

function computeSpaceValueRows(
  workflowRows: readonly WorkflowValueRow[],
  spaces: readonly DisplayableSpace[],
  displayNameFn: (s: DisplayableSpace) => string
): SpaceValueRow[] {
  const bySpace = new Map<
    string,
    { build: number; run: number; count: number }
  >();
  for (const row of workflowRows) {
    const entry = bySpace.get(row.spaceId) ?? {
      build: 0,
      run: 0,
      count: 0,
    };
    entry.build += row.buildValueUsd;
    entry.run += row.runValueUsd;
    entry.count += 1;
    bySpace.set(row.spaceId, entry);
  }

  const spaceById = new Map(spaces.map((s) => [s.id, s]));
  const rows: SpaceValueRow[] = [];
  for (const [spaceId, vals] of bySpace) {
    const space = spaceById.get(spaceId);
    if (!space) continue;
    rows.push({
      spaceId,
      space,
      label: displayNameFn(space),
      totalValueUsd: vals.build + vals.run,
      buildValueUsd: vals.build,
      runValueUsd: vals.run,
      workflowCount: vals.count,
    });
  }

  return rows.sort(
    (a, b) =>
      b.totalValueUsd - a.totalValueUsd || a.label.localeCompare(b.label)
  );
}

function sourceLabel(source: "llm" | "manual" | null): string {
  if (source === "manual") return "set by user";
  if (source === "llm") return "AI estimate";
  return "not set";
}

function LoadingPlaceholder() {
  return (
    <>
      <Skeleton
        aria-hidden
        style={{ width: "100%", height: 72, borderRadius: 12, flexShrink: 0 }}
      />
      <Skeleton
        aria-hidden
        style={{ width: "100%", flex: 1, minHeight: 200, borderRadius: 12 }}
      />
    </>
  );
}

export function Value() {
  const { window: windowParam = "30" } = useCurrentUrl(routes.monitoring.value);
  const since = roundedHoursAgo(windowMs(windowParam) / 3_600_000);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const zero = useZero();
  const canManageAI = useHasAccess("tenant:ai_providers_manage");
  const [usageRecords, usageQuery] = useQuery(
    queries.monitoringAiUsageDaily({ since })
  );
  const [pricingRows, pricingQuery] = useQuery(queries.aiModelPricingRows());
  const [budgets, budgetsQuery] = useQuery(queries.monitoringAiBudgets());
  const [workflows, workflowsQuery] = useQuery(
    queries.monitoringWorkflowValue()
  );
  const [executionVolume, executionVolumeQuery] = useQuery(
    queries.monitorWorkflowDailyStats({ since })
  );
  const [spaces] = useQuery(queries.spacesLite({ includeAdminSpaces: true }));
  const displayName = useSpaceDisplayName();

  // Hold the last completed windowed result so changing the time window swaps
  // data in place instead of blanking while the new window's query reloads.
  const lastUsage = useRef(usageRecords);
  if (usageQuery.type === "complete") lastUsage.current = usageRecords;
  const usageRecordsView =
    usageQuery.type === "complete" ? usageRecords : lastUsage.current;

  const lastExecutionVolume = useRef(executionVolume);
  if (executionVolumeQuery.type === "complete") {
    lastExecutionVolume.current = executionVolume;
  }
  const executionVolumeView =
    executionVolumeQuery.type === "complete"
      ? executionVolume
      : lastExecutionVolume.current;

  const pricing = useMemo(
    () => buildModelPricingLookup(pricingRows ?? []),
    [pricingRows]
  );
  // Value by space, the space filter, and the space dropdown are all team-only,
  // so scope the summary and table to team-space workflows too — otherwise the
  // header total would count personal-space value the space chart omits.
  const teamWorkflows = useMemo(
    () => (workflows ?? []).filter((w) => w.space?.mode !== "personal"),
    [workflows]
  );
  const workflowInputs = useMemo(
    () => teamWorkflows.map(toWorkflowValueWorkflow),
    [teamWorkflows]
  );
  // Run value is per successful run, so exclude failed runs from the count.
  const executionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const workflow of executionVolumeView ?? []) {
      for (const row of workflow.runDailyStats) {
        const successful = (row.runCount ?? 0) - (row.errorRunCount ?? 0);
        if (successful <= 0) continue;
        counts.set(workflow.id, (counts.get(workflow.id) ?? 0) + successful);
      }
    }
    return counts;
  }, [executionVolumeView]);
  const hourlyRateUsd = useMemo(
    () => productivityRateFromBudgets(budgets ?? []),
    [budgets]
  );
  const [rateInput, setRateInput] = useState<string | null>(null);

  const valueRows = useMemo(
    () =>
      computeWorkflowAiValueRows(
        usageRecordsView ?? [],
        workflowInputs,
        executionCounts,
        pricing,
        hourlyRateUsd
      ),
    [usageRecordsView, workflowInputs, executionCounts, pricing, hourlyRateUsd]
  );

  const visibleValueRows = useMemo(
    () =>
      selectedSpaceId
        ? valueRows.filter((r) => r.spaceId === selectedSpaceId)
        : valueRows,
    [valueRows, selectedSpaceId]
  );

  const valueSummary = useMemo(
    () => computeAiValueSummary(visibleValueRows, "all"),
    [visibleValueRows]
  );

  const workflowRows = useMemo(
    () => computeWorkflowValueRows(valueRows, teamWorkflows),
    [valueRows, teamWorkflows]
  );

  const teamSpaces = useMemo(
    () => (spaces ?? []).filter((s) => s.mode !== "personal"),
    [spaces]
  );

  const spaceRows = useMemo(
    () => computeSpaceValueRows(workflowRows, teamSpaces, displayName),
    [workflowRows, teamSpaces, displayName]
  );

  const filteredWorkflowRows = useMemo(
    () =>
      selectedSpaceId
        ? workflowRows.filter((r) => r.spaceId === selectedSpaceId)
        : workflowRows,
    [workflowRows, selectedSpaceId]
  );

  const selectedSpaceIndex = selectedSpaceId
    ? spaceRows.findIndex((r) => r.spaceId === selectedSpaceId)
    : -1;
  const selectedSpace =
    selectedSpaceIndex >= 0 ? spaceRows[selectedSpaceIndex] : null;

  const [sortColumn, setSortColumn] = useState<string | null>("total");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection(column === "name" ? "asc" : "desc");
    }
  };

  const sortedWorkflowRows = useMemo(() => {
    const rows = [...filteredWorkflowRows];
    if (!sortColumn) return rows;
    const dir = sortDirection === "asc" ? 1 : -1;
    const metric = (r: WorkflowValueRow) =>
      sortColumn === "build"
        ? r.buildValueUsd
        : sortColumn === "run"
          ? r.runValueUsd
          : r.totalValueUsd;
    rows.sort((a, b) =>
      sortColumn === "name"
        ? a.name.localeCompare(b.name) * dir
        : (metric(a) - metric(b)) * dir || a.name.localeCompare(b.name)
    );
    return rows;
  }, [filteredWorkflowRows, sortColumn, sortDirection]);

  const spaceFilters: FilterDef[] = useMemo(
    () => [
      {
        kind: "single",
        key: "space",
        label: "Space",
        icon: <Icon name="hash" size={16} />,
        options: spaceRows.map((r) => ({
          id: r.spaceId,
          label: r.label,
          menuLeft: <Icon name={spaceIcon(r.space.mode)} size={16} />,
          pillLeft: <Icon name={spaceIcon(r.space.mode)} size={12} />,
        })),
        value: selectedSpaceId,
        onSelect: (id) => setSelectedSpaceId(id),
        onClear: () => setSelectedSpaceId(null),
      },
    ],
    [spaceRows, selectedSpaceId]
  );

  const spaceSegments = useMemo<StatusSegment[]>(
    () =>
      spaceRows.map((row, i) => ({
        key: row.spaceId,
        count: row.totalValueUsd,
        color: SPACE_COLORS[i % SPACE_COLORS.length]!,
        label: row.label,
        details: [
          { label: "Total", value: formatValueUsd(row.totalValueUsd) },
          { label: "Build", value: formatValueUsd(row.buildValueUsd || null) },
          { label: "Run", value: formatValueUsd(row.runValueUsd || null) },
          { label: "Workflows", value: String(row.workflowCount) },
        ],
      })),
    [spaceRows]
  );
  const spaceValueTotal = useMemo(
    () => spaceRows.reduce((sum, r) => sum + r.totalValueUsd, 0),
    [spaceRows]
  );

  const buildValue = useMemo(
    () => computeAiValueSummary(visibleValueRows, "build").grossValueUsd,
    [visibleValueRows]
  );
  const runValue = useMemo(
    () => computeAiValueSummary(visibleValueRows, "run").grossValueUsd,
    [visibleValueRows]
  );

  const isLoading =
    usageQuery.type !== "complete" ||
    pricingQuery.type !== "complete" ||
    budgetsQuery.type !== "complete" ||
    workflowsQuery.type !== "complete" ||
    executionVolumeQuery.type !== "complete";

  // Only block the whole page on the first load; window changes refetch the
  // windowed queries but should keep the rendered content (with held data).
  const hasLoadedOnce = useRef(false);
  if (!isLoading) hasLoadedOnce.current = true;

  const tenantBudget = (budgets ?? []).find(
    (b) => b.scopeKind === "tenant" && !b.aiProviderId
  );

  async function commitRate(value: string) {
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed) || parsed <= 0) {
      setRateInput(null);
      return;
    }
    setRateInput(null);
    if (parsed === hourlyRateUsd) return;
    await zero.mutate(
      sharedMutators.updateAIBudget({
        id: tenantBudget?.id,
        scopeKind: "tenant",
        productivityHourlyRate: parsed,
      })
    );
  }

  function adjustRate(delta: number) {
    const parsed = parseFloat(rateInput ?? String(hourlyRateUsd));
    const base = Number.isFinite(parsed) ? parsed : hourlyRateUsd;
    commitRate(String(Math.max(RATE_STEP, base + delta)));
  }

  function setWindow(value: string) {
    navigate(routes.monitoring.value, { window: value }, { replace: true });
  }

  return (
    <MonitoringLayout>
      <Content>
        {isLoading && !hasLoadedOnce.current ? (
          <LoadingPlaceholder />
        ) : (
          <SummaryCard>
            <SummaryHeader>
              <StatsRow>
                <StatCell>
                  <StatLabel
                    label="Total"
                    tooltip={VALUE_STAT_TOOLTIPS.total}
                  />
                  <ValueBox>
                    <Text variant="display-md" color="var(--text-primary)">
                      {formatSignedValueUsd(valueSummary.grossValueUsd ?? 0)}
                    </Text>
                  </ValueBox>
                </StatCell>
                {(
                  [
                    { key: "build", value: buildValue },
                    { key: "run", value: runValue },
                  ] as const
                ).map(({ key, value }) => (
                  <StatCell key={key}>
                    <StatLabel
                      label={AI_USAGE_ACTIVITY_LABELS[key as AiUsageActivity]}
                      tooltip={VALUE_STAT_TOOLTIPS[key]}
                    />
                    <ValueBox>
                      <Text variant="display-md">
                        {formatSignedValueUsd(value ?? 0)}
                      </Text>
                    </ValueBox>
                  </StatCell>
                ))}
                <StatCell>
                  <StatLabel
                    label="Workflows"
                    tooltip={VALUE_STAT_TOOLTIPS.workflows}
                  />
                  <ValueBox>
                    <Text variant="display-md">
                      {valueSummary.workflowCount}
                    </Text>
                  </ValueBox>
                </StatCell>
              </StatsRow>
              <StatRow>
                <StatCell>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Space
                  </Text>
                  <Dropdown
                    align="bottom"
                    opener={
                      <ControlButton type="button">
                        {selectedSpace ? (
                          <ScopeDot
                            style={{
                              background:
                                SPACE_COLORS[
                                  selectedSpaceIndex % SPACE_COLORS.length
                                ],
                            }}
                          />
                        ) : (
                          <Icon
                            name="layers"
                            size={12}
                            color="var(--text-primary)"
                          />
                        )}
                        <Text variant="text-small-md">
                          {selectedSpace ? selectedSpace.label : "All spaces"}
                        </Text>
                        <Icon
                          name="chevron-down"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      </ControlButton>
                    }
                    sections={[
                      {
                        items: [
                          {
                            type: "button" as const,
                            key: "__all__",
                            left: (
                              <Icon
                                name="layers"
                                size={16}
                                color="var(--text-primary)"
                              />
                            ),
                            label: "All spaces",
                            selected: selectedSpaceId === null,
                            onClick: () => setSelectedSpaceId(null),
                          },
                          ...spaceRows.map((r, i) => ({
                            type: "button" as const,
                            key: r.spaceId,
                            left: (
                              <ScopeDot
                                style={{
                                  background:
                                    SPACE_COLORS[i % SPACE_COLORS.length],
                                }}
                              />
                            ),
                            label: r.label,
                            selected: selectedSpaceId === r.spaceId,
                            onClick: () => setSelectedSpaceId(r.spaceId),
                          })),
                        ],
                      },
                    ]}
                  />
                </StatCell>
                <StatCell>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Time range
                  </Text>
                  <Dropdown
                    align="bottom"
                    opener={
                      <ControlButton type="button">
                        <Text variant="text-small-md">
                          {windowLabel(windowParam)}
                        </Text>
                        <Icon
                          name="chevron-down"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      </ControlButton>
                    }
                    sections={[
                      {
                        items: WINDOW_OPTIONS.map((opt) => ({
                          type: "button" as const,
                          key: opt.value,
                          left: null,
                          label: opt.label,
                          selected: windowParam === opt.value,
                          onClick: () => setWindow(opt.value),
                        })),
                      },
                    ]}
                  />
                </StatCell>
                <StatCell>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Hourly rate ($)
                  </Text>
                  <Stepper>
                    <StepButton
                      data-no-pill
                      aria-label="Decrease hourly rate"
                      disabled={
                        !canManageAI ||
                        (parseFloat(rateInput ?? String(hourlyRateUsd)) ||
                          hourlyRateUsd) <= RATE_STEP
                      }
                      onPress={() => adjustRate(-RATE_STEP)}
                    >
                      <Icon
                        name="minus"
                        size={16}
                        color="var(--text-secondary)"
                      />
                    </StepButton>
                    <Input
                      value={rateInput ?? String(hourlyRateUsd)}
                      inputMode="numeric"
                      disabled={!canManageAI}
                      onChange={(e) => setRateInput(e.target.value)}
                      onBlur={(e) => commitRate(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          commitRate(e.currentTarget.value);
                        }
                        if (e.key === "Escape") setRateInput(null);
                      }}
                      style={
                        canManageAI
                          ? RATE_INPUT_STYLE
                          : { ...RATE_INPUT_STYLE, opacity: 0.4 }
                      }
                    />
                    <StepButton
                      data-no-pill
                      aria-label="Increase hourly rate"
                      disabled={!canManageAI}
                      onPress={() => adjustRate(RATE_STEP)}
                    >
                      <Icon
                        name="plus"
                        size={16}
                        color="var(--text-secondary)"
                      />
                    </StepButton>
                  </Stepper>
                </StatCell>
              </StatRow>
            </SummaryHeader>
            <StatusBreakdownChart
              title="Value by space"
              total={spaceValueTotal}
              segments={spaceSegments}
              formatValue={formatValueUsd}
              embedded
              wrapLegend
              showLegendValue={false}
              placeholder={spaceRows.length === 0}
              activeKey={selectedSpaceId}
              onSegmentClick={(s) =>
                setSelectedSpaceId(selectedSpaceId === s.key ? null : s.key)
              }
            />
            <TableCard>
              <ScrollArea>
                <ListTable
                  headers={{
                    name: "Workflow",
                    build: "Build",
                    run: "Run",
                    total: "Total",
                    actions: "",
                  }}
                  columns={{
                    name: "flex",
                    build: "md",
                    run: "md",
                    total: "md",
                    actions: "sm",
                  }}
                  sortableColumns={["name", "build", "run", "total"]}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  headerActions={
                    workflowRows.length > 0 ? (
                      <ListFilter filters={spaceFilters} />
                    ) : undefined
                  }
                  emptyState={
                    <Text variant="text-regular" color="var(--text-secondary)">
                      {selectedSpaceId
                        ? "No workflows with value in this space."
                        : "No workflows with value in this window."}
                    </Text>
                  }
                  items={sortedWorkflowRows.map((row) => ({
                    key: row.workflowId,
                    link: makeUrl(routes.modal.workflowValue, {
                      id: row.workflowId,
                    }),
                    name: (hover) => (
                      <NameCell>
                        <WorkflowBadge
                          workflow={{
                            id: row.workflowId,
                            badgeSeed: row.badgeSeed,
                            pausedAt: row.pausedAt,
                            branches: row.branches,
                          }}
                          hover={hover}
                        />
                        <NameBody>
                          <Text variant="text-regular-md" ellipsis>
                            {row.name}
                          </Text>
                          {row.space && (
                            <Text
                              variant="text-small"
                              color="var(--text-secondary)"
                              ellipsis
                            >
                              {displayName(row.space)}
                            </Text>
                          )}
                        </NameBody>
                      </NameCell>
                    ),
                    build: (
                      <Tooltip
                        content={`${row.buildMinutes ?? "–"} min to build${META_SEP}${sourceLabel(row.buildSource)}`}
                      >
                        <SubValue>
                          <Text variant="text-regular-md">
                            {formatValueUsd(row.buildValueUsd || null)}
                          </Text>
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                          >
                            {row.buildMinutes != null
                              ? `${row.buildMinutes}m`
                              : "–"}
                          </Text>
                        </SubValue>
                      </Tooltip>
                    ),
                    run: (
                      <Tooltip
                        content={`${row.runMinutes ?? "–"} min saved/run${META_SEP}${sourceLabel(row.runSource)}`}
                      >
                        <SubValue>
                          <Text variant="text-regular-md">
                            {formatValueUsd(row.runValueUsd || null)}
                          </Text>
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                          >
                            {row.runMinutes != null
                              ? `${row.runMinutes}m/run`
                              : "–"}
                          </Text>
                        </SubValue>
                      </Tooltip>
                    ),
                    total: (
                      <Text variant="text-regular-md" color="var(--green-600)">
                        {formatValueUsd(row.totalValueUsd)}
                      </Text>
                    ),
                    actions: null,
                  }))}
                />
              </ScrollArea>
              {filteredWorkflowRows.length > 0 && (
                <SumRow>
                  <SumCell>
                    <Text variant="text-regular-md">Total</Text>
                  </SumCell>
                  <SumCell>
                    <Text variant="text-regular-md">
                      {formatValueUsd(
                        filteredWorkflowRows.reduce(
                          (s, r) => s + r.buildValueUsd,
                          0
                        ) || null
                      )}
                    </Text>
                  </SumCell>
                  <SumCell>
                    <Text variant="text-regular-md">
                      {formatValueUsd(
                        filteredWorkflowRows.reduce(
                          (s, r) => s + r.runValueUsd,
                          0
                        ) || null
                      )}
                    </Text>
                  </SumCell>
                  <SumCell>
                    <Text variant="text-regular-md" color="var(--green-600)">
                      {formatValueUsd(
                        filteredWorkflowRows.reduce(
                          (s, r) => s + r.totalValueUsd,
                          0
                        )
                      )}
                    </Text>
                  </SumCell>
                  <SumCell />
                </SumRow>
              )}
            </TableCard>
          </SummaryCard>
        )}
      </Content>
    </MonitoringLayout>
  );
}
